import { useEffect, useMemo, useRef } from 'react';
import { DataTable, type DataTablePagination } from '@odo/shared-ui';
import type { VirtualAppointmentOverviewItem } from '../../../services/virtualAppointmentsOverview';
import {
    type OverviewListState,
    type OverviewTableMode
} from '../../../hooks/useVirtualAppointmentsOverview';
import { fullTypeTitle } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { summaryFromDto } from '../../virtual-appointments/virtualAppointmentSummary';
import { OverviewEmptyState, OverviewErrorState } from './OverviewTableStates';
import type { OverviewSort, OverviewSortKey } from './overviewSort';
import type { OverviewTabConfig } from './overviewTabs';
import { buildOverviewColumns } from './virtualAppointmentsOverviewColumns';

const SKELETON_ROWS = 8;
// The table is the modal's main surface, so the DataTable card chrome is dropped (cx only joins).
const EDGE_TO_EDGE_CARD = '!border-0 !rounded-none !shadow-none';
const noop = (): void => undefined;

export interface OverviewPaging {
    mode: OverviewTableMode;
    onModeChange: (mode: OverviewTableMode) => void;
    page: number;
    pageSize: number;
    onPage: (page: number) => void;
    /** Changes whenever the rows start over (view, filters, search, sort, page), to scroll to the top. */
    resetKey: string;
}

interface VirtualAppointmentsOverviewTableProps {
    tab: OverviewTabConfig;
    list: OverviewListState;
    paging: OverviewPaging;
    sort: OverviewSort;
    onSort: (column: OverviewSortKey) => void;
    selectedId: string | null;
    onSelect: (item: VirtualAppointmentOverviewItem) => void;
    todayIso: string;
    doctorByPracticeId: ReadonlyMap<string, string>;
    filtersActive: boolean;
    onClearFilters: () => void;
}

function rowLabel(item: VirtualAppointmentOverviewItem): string {
    return `${fullTypeTitle(summaryFromDto(item))} for ${item.patient.name}`;
}

// Infinite mode only reads `total` (loaded-vs-total); paged mode drives the pager from it.
function paginationFor(
    list: OverviewListState,
    paging: OverviewPaging
): DataTablePagination | undefined {
    if (list.total === undefined) return undefined;
    if (paging.mode === 'infinite') {
        return { page: 1, totalPages: 1, total: list.total, onPage: noop };
    }
    const totalPages = Math.max(1, Math.ceil(list.total / paging.pageSize));
    return { page: paging.page, totalPages, total: list.total, onPage: paging.onPage };
}

export function VirtualAppointmentsOverviewTable({
    tab,
    list,
    paging,
    sort,
    onSort,
    selectedId,
    onSelect,
    todayIso,
    doctorByPracticeId,
    filtersActive,
    onClearFilters
}: VirtualAppointmentsOverviewTableProps) {
    const compact = selectedId !== null;
    const columns = useMemo(
        () => buildOverviewColumns({ tab, compact, todayIso, doctorByPracticeId }),
        [tab, compact, todayIso, doctorByPracticeId]
    );
    const scrollRef = useRef<HTMLDivElement>(null);
    const refs = useMemo(() => ({ scroll: scrollRef }), []);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 0 });
    }, [paging.resetKey]);

    return (
        <DataTable
            label={`Virtual appointments: ${tab.label}`}
            className="flex-1 min-h-0"
            cardClassName={EDGE_TO_EDGE_CARD}
            columns={columns}
            rows={list.items}
            getRowKey={(item) => item.id}
            sort={{ by: sort.by, order: sort.order, onSort }}
            mode={paging.mode}
            onModeChange={paging.onModeChange}
            pagination={paginationFor(list, paging)}
            onLoadMore={list.loadMore}
            hasMore={list.hasMore}
            isLoadingMore={list.isLoadingMore}
            loadMoreLabel="Load more VAs"
            rowAction={{ mode: 'button', onActivate: onSelect, getLabel: rowLabel }}
            rowClassName={(item) =>
                `font-inter text-xs text-gray-700 ${item.id === selectedId ? '!bg-blue-50' : ''}`
            }
            isLoading={list.isLoading}
            skeletonRowCount={SKELETON_ROWS}
            emptyStateCellClassName="py-16"
            emptyState={
                list.isError ? (
                    <OverviewErrorState onRetry={list.retry} />
                ) : (
                    <OverviewEmptyState
                        title={tab.emptyTitle}
                        text={tab.emptyText}
                        filtersActive={filtersActive}
                        onClearFilters={onClearFilters}
                    />
                )
            }
            refs={refs}
        />
    );
}
